import { useState, useEffect, useContext } from "react";
import "../common/common.scss";
import FormRendererContext from "../../services/context/FormRendererContext";
import { currentFormattedDateAndTime, trackEvent } from "../../utils/mixPanel";
import { isUrl } from "../../utils/generalUtils";

// Import all your existing components
import TextField from "./TextField/TextField";
import TextArea from "./TextArea/TextArea";
import NumberInput from "../common/Number";
import HeightField from "../common/Height";
import AmericanPhoneField from "../common/AmericanPhoneField";
import PhoneField from "./PhoneField/PhoneField";
import Password from "../common/Password";
import DateInput from "../common/DateInput";
// import Email from "../common/Email";
import EmailField from "./EmailField/EmailField";
import SingleCheckBox from "../common/SingleCheckBox";
import DropDownSelector from "../common/DropDownSelector";
import Telephone from "../common/Telephone";
import DropDownWithSearch from "../common/DropDownWithSearch";
import RadioSelector from "../common/RadioSelector";
import ContentRenderer from "../common/contentRenderer";
// import UploadFile from "../common/UploadFile";
import UploadFile from "./UploadFile";
import CheckBoxSelector from "../common/CheckBoxSelector";
import DynamicContentRenderer from "../common/DynamicContentRenderer";
import HealthHistoryAllergyComponent from "../common/HealthHistoryAllergyComponent";
import HealthHistoryMedicationComponent from "../common/HealthHistoryMedicationComponent";
import Content from "./Content/Content";

const Columns = ({ 
  data, 
  setAnswer, 
  formValidation = true, 
  formClass,
  allDepartmentsForSpecificForm = [],
  allReasonsForSpecificForm = [],
}) => {
  const [columnData, setColumnData] = useState(data.answer?.value || {});
  const [validForm, setFormValidation] = useState(formValidation);
  const { answerData } = useContext(FormRendererContext);

  const handleFocus = () => {
    trackEvent(`columns ${data?.label} clicked`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
    });
  };

  const handleBlur = () => {
    trackEvent(`columns ${data?.label} filled`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
      data: "Answered",
    });
  };

  const updateColumnData = (componentKey, value) => {
    const newColumnData = {
      ...columnData,
      [componentKey]: value.value
    };
    setColumnData(newColumnData);
    setAnswer(data, { value: newColumnData });
    setFormValidation(true);
  };

  const getComponentByType = (item) => {
    let FormComponent = null;
    let specialProps = {};

    switch (item?.type) {
      case "textfield":
        FormComponent = TextField;
        break;
      case "heightField":
        FormComponent = HeightField;
        break;
      case "americanPhoneField":
        FormComponent = AmericanPhoneField;
        break;
      case "textarea":
        FormComponent = TextArea;
        break;
      case "password":
        FormComponent = Password;
        break;
      case "datetime":
        FormComponent = DateInput;
        break;
      case "dob":
        FormComponent = DateInput;
        break;
      case "email":
        FormComponent = EmailField;
        break;
      case "checkbox":
        FormComponent = SingleCheckBox;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "select":
        FormComponent = DropDownSelector;
        // Special case for department selector
        if (item?.key === "patientDepartmentForSchedulerForSpecificForm") {
          specialProps = {
            data: {
              ...item,
              data: { values: allDepartmentsForSpecificForm },
            },
          };
        }
        break;
      case "number":
        FormComponent = NumberInput;
        break;
      case "phoneNumber":
        FormComponent = PhoneField;
        break;
      case "dropDownWithSearch":
        FormComponent = DropDownWithSearch;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "radio":
        FormComponent = RadioSelector;
        // Special case for reason selector
        if (item?.key === "reasonForVisitForScheduler") {
          specialProps = {
            data: {
              ...item,
              values: allReasonsForSpecificForm,
            },
          };
        }
        break;
      case "content":
        FormComponent = Content;
        break;
      case "file":
        FormComponent = UploadFile;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "selectboxes":
        FormComponent = CheckBoxSelector;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "dynamicContentRenderer":
        FormComponent = DynamicContentRenderer;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "healthHistoryAllergyComponent":
        FormComponent = HealthHistoryAllergyComponent;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "healthHistoryMedicationComponent":
        FormComponent = HealthHistoryMedicationComponent;
        specialProps = { formValidation: item?.formValidation };
        break;
      default:
        return { FormComponent: null, specialProps: {} };
    }

    return { FormComponent, specialProps };
  };

  const renderComponent = (component) => {
    const { FormComponent, specialProps } = getComponentByType(component);
    
    if (!FormComponent) {
      return null;
    }

    const componentData = {
      ...component,
      answer: { value: columnData?.[component.key] || "" }
    };

    // Handle special case for select component data
    const itemData =
      component.type === "select" &&
      component.key === "patientDepartmentForSchedulerForSpecificForm"
        ? {
            ...component,
            data: { values: allDepartmentsForSpecificForm },
          }
        : component.type === "radio" &&
          component.key === "reasonForVisitForScheduler"
        ? {
            ...component,
            values: allReasonsForSpecificForm,
          }
        : componentData;

    const componentSetAnswer = (answerValue) => {
      updateColumnData(component.key, answerValue);
    };

    return (
      <FormComponent
        key={component.key}
        data={specialProps.data || itemData}
        setAnswer={(q, a)=>componentSetAnswer(a)}
        onChange={(val)=>componentSetAnswer({ value: val })}
        formValidation={true}
        formClass="mb-0"
        {...specialProps}
      />
    );
  };

  // Calculate Tailwind classes based on width and responsive size
  const getColumnClasses = (column) => {
    const { width = 6, size = "md", offset = 0 } = column;
    
    let classes = [];
    
    // Convert Form.io width (1-12) to Tailwind fraction classes
    const widthMap = {
      1: "1/12",
      2: "2/12", // or "1/6"
      3: "3/12", // or "1/4"
      4: "4/12", // or "1/3"
      5: "5/12",
      6: "6/12", // or "1/2"
      7: "7/12",
      8: "8/12", // or "2/3"
      9: "9/12", // or "3/4"
      10: "10/12", // or "5/6"
      11: "11/12",
      12: "12/12" // or "full"
    };
    
    const tailwindWidth = widthMap[width] || "6/12";
    
    // Base responsive width classes
    if (size === "xs") classes.push(`w-${tailwindWidth}`);
    else if (size === "sm") classes.push(`sm:w-${tailwindWidth}`);
    else if (size === "md") classes.push(`md:w-${tailwindWidth}`);
    else if (size === "lg") classes.push(`lg:w-${tailwindWidth}`);
    else if (size === "xl") classes.push(`xl:w-${tailwindWidth}`);
    else classes.push(`md:w-${tailwindWidth}`); // Default to md
    
    // Add base mobile width (full width on mobile by default)
    if (size !== "xs") {
      classes.push("w-full");
    }
    
    // Offset classes using margin-left
    if (offset > 0) {
      const offsetWidth = widthMap[offset] || "0";
      if (size === "xs") classes.push(`ml-${offsetWidth.replace('/', '-')}`);
      else classes.push(`${size}:ml-${offsetWidth.replace('/', '-')}`);
    }
    
    return classes.join(" ");
  };

  useEffect(() => {
    setFormValidation(formValidation);
    if (data?.answer && data?.answer?.value) {
      setColumnData(data.answer.value);
      setFormValidation(true);
    } else if (data?.defaultValue) {
      setColumnData(data.defaultValue);
    }

    if (answerData?.[data?.key]) {
      setColumnData(answerData[data.key].value);
    }
  }, [data, formValidation]);

  // Check if any column has required components
  const hasRequiredComponents = data?.columns?.some(column => 
    column.components?.some(comp => comp?.validate?.required)
  );

  return (
    <div className={`w-full ${formClass}`}>
      {/* Label - only show if explicitly provided */}
      {data?.label && (
        <div className="form-title py-1">
          {data?.label}
          {hasRequiredComponents ? (
            <span className={!validForm ? "text-red-700" : "text-gray-600"}>
              {" "}
              *
            </span>
          ) : (
            <></>
          )}
        </div>
      )}
      
      {/* Validation message */}
      {hasRequiredComponents && !validForm ? (
        <span className="text-red-700 font-light text-xs">
          {data?.invalidMessage || "Required Field"}
        </span>
      ) : (
        <></>
      )}

      {/* Columns Container */}
      <div 
        className={`columns-container ${data?.autoAdjust ? 'auto-adjust' : ''}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <div className="flex flex-wrap -mx-2">
          {data?.columns?.map((column, columnIndex) => (
            <div
              key={columnIndex}
              className={`px-2 ${getColumnClasses(column)}`}
            >
              {/* Render all components in this column */}
              {column?.components?.map((component) => (
                <div key={component.key} className="mb-3">
                  {renderComponent(component)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      {data?.description && (
        <div className="text-xs text-gray-500 leading-relaxed mt-1">
          {isUrl(data.description) ? (
            <img
              src={data.description}
              alt="Description"
              className="mt-1 rounded"
              style={{ maxHeight: "200px" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentNode.textContent = data.description;
              }}
            />
          ) : (
            data.description
          )}
        </div>
      )}
    </div>
  );
};

export default Columns;